<template>
  <div class="plan-comparison">
    <v-simple-table class="comparison-table">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="feature-col">Feature</th>
            <th
              v-for="(plan, j) in plans"
              :key="plan.id"
              class="text-center"
              :class="{ 'pro-col': isProIndex(j) }"
            >
              {{ planName(plan) }}
              <div class="th-price">{{ formatPlanPrice(plan) }}{{ isFreePlan(plan) ? " forever" : "/mo" }}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in tableRows" :key="i">
            <td class="feature-col">{{ row.label }}</td>
            <td
              v-for="(cell, j) in row.cells"
              :key="j"
              class="text-center"
              :class="{ 'pro-col': isProIndex(j) }"
            >
              <template v-if="cell.bool !== undefined">
                <v-icon v-if="cell.bool" small color="success"
                  >mdi-check</v-icon
                >
                <span v-else class="muted">-</span>
              </template>
              <template v-else>{{ cell.text }}</template>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import { formatPlanPrice, isFreePlan } from "@shared/utils/planPricing";
import { formatPlanName } from "@shared/utils/planLabels";

// Credits one typical job costs under the cost-plus rule. The
// real figure arrives on the credit-balance payload
// (`reference_video_credits`, derived from metered usage); this is only
// the fallback while it loads.
const DEFAULT_REFERENCE_VIDEO_CREDITS = 390;

// Fallbacks only - the real values arrive on the credit-balance payload
// (`free_tier`, `credit_packs`). The free plan's `credits_per_month` is 0
// because its grant is one-time, so every free cell derives from these
// instead of from the plan row.
const DEFAULT_FREE_STARTER_CREDITS = 50;
const DEFAULT_SUBSCRIBER_PER_DOLLAR = 10;
const DEFAULT_NON_SUBSCRIBER_PER_DOLLAR = 7;

// Rounds DOWN to a clean "feel" number so a cell never over-promises.
function roundNiceCount(n) {
  if (!isFinite(n) || n <= 0) return 0;
  if (n >= 1000) return Math.floor(n / 100) * 100;
  if (n >= 100) return Math.floor(n / 50) * 50;
  if (n >= 10) return Math.floor(n / 5) * 5;
  return Math.floor(n);
}

export default {
  name: "PlanComparisonTable",
  props: {
    plans: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    referenceVideoCredits() {
      var cb =
        this.$store && this.$store.getters
          ? this.$store.getters.creditBalance
          : null;
      var n = cb ? Number(cb.reference_video_credits) : NaN;
      return isFinite(n) && n > 0 ? n : DEFAULT_REFERENCE_VIDEO_CREDITS;
    },
    proIndex() {
      return this.plans.findIndex(p => this.isPro(p));
    },
    creditBalanceInfo() {
      return this.$store && this.$store.getters
        ? this.$store.getters.creditBalance
        : null;
    },
    // Steady-state free allowance (not the higher first-week intro rate) - a
    // comparison table should show what the free plan gives you ongoing.
    freeStarterCredits() {
      var grant = this.$store.getters.starterCreditGrant;
      var v = grant ? Number(grant.amount) : NaN;
      return isFinite(v) && v >= 0 ? v : DEFAULT_FREE_STARTER_CREDITS;
    },
    packRates() {
      var cb = this.creditBalanceInfo;
      var cp = cb ? cb.credit_packs : null;
      var sub = cp ? Number(cp.subscriber_per_dollar) : NaN;
      var non = cp ? Number(cp.non_subscriber_per_dollar) : NaN;
      return {
        subscriber:
          isFinite(sub) && sub > 0 ? sub : DEFAULT_SUBSCRIBER_PER_DOLLAR,
        nonSubscriber:
          isFinite(non) && non > 0 ? non : DEFAULT_NON_SUBSCRIBER_PER_DOLLAR
      };
    },
    // Each row: { label, cells: [{ text } | { bool }] }, one cell per plan in
    // the order received. Differentiators first, then "included on every plan".
    tableRows() {
      return [
        {
          label: "Monthly price",
          cells: this.plans.map(p => ({ text: this.formatPlanPrice(p) }))
        },
        // The free plan's credits are awarded once rather than monthly. Cells carry
        // their own unit rather than the row label, which lets one row compare
        // a one-time grant against monthly allowances honestly.
        {
          label: "Credits",
          cells: this.plans.map(p => ({ text: this.creditsLabel(p) }))
        },
        {
          label: "≈ " + this.$config.UNIT_NOUN_PLURAL,
          cells: this.plans.map(p => ({
            text: this.usageEstimateLabel(p, this.creditEstimate(p).videos)
          }))
        },
        {
          label: "Concurrent " + this.$config.UNIT_NOUN_PLURAL,
          cells: this.plans.map(p => ({ text: String(this.concurrentJobs(p)) }))
        },
        {
          label: "Priority support",
          cells: this.plans.map(p => ({ bool: this.hasPrioritySupport(p) }))
        },
        {
          label: "API access",
          cells: this.plans.map(() => ({ bool: true }))
        },
        {
          label: "AI agent (MCP)",
          cells: this.plans.map(() => ({ bool: true }))
        },
        // Credit packs are open to every plan (subscription_billing's
        // credits.purchase has no plan gate) - subscribers simply check out at
        // the better rate. Showing a "-" for free contradicted the paywall,
        // which sells free users a pack.
        {
          label: "Buy additional credits",
          cells: this.plans.map(p => ({ text: this.packRateLabel(p) }))
        }
      ];
    }
  },
  methods: {
    formatPlanPrice(plan) { return formatPlanPrice(plan, this.$config); },
    isFreePlan,
    planName(p) {
      return formatPlanName(p.name);
    },
    isPro(p) {
      return !!(p && p.name && p.name.toLowerCase() === "pro");
    },
    isProIndex(j) {
      return j === this.proIndex;
    },
    features(p) {
      return (p && p.features) || null;
    },
    // Credits the plan actually grants: once for free, per month for everyone
    // else. Reading `credits_per_month` for free would
    // give 0 and render "0 jobs", which is simply untrue.
    planCredits(p) {
      if (this.isFreePlan(p)) return this.freeStarterCredits;
      return Number(p && p.credits_per_month) || 0;
    },
    creditEstimate(p) {
      var credits = this.planCredits(p);
      return {
        videos: roundNiceCount(credits / this.referenceVideoCredits)
      };
    },
    perPeriod(p, count) {
      return count.toLocaleString() + " / mo";
    },
    usageEstimateLabel(p, count) {
      return this.isFreePlan(p)
        ? "Varies by job size"
        : this.perPeriod(p, count);
    },
    creditsLabel(p) {
      if (this.isFreePlan(p)) return this.planCredits(p) + " one time";
      return this.perPeriod(p, this.planCredits(p));
    },
    packRateLabel(p) {
      var rate = this.isFreePlan(p)
        ? this.packRates.nonSubscriber
        : this.packRates.subscriber;
      return rate + " credits / $1";
    },
    concurrentJobs(p) {
      var f = this.features(p);
      if (f && f.concurrent_jobs) {
        return parseInt(f.concurrent_jobs, 10);
      }
      return 1;
    },
    hasPrioritySupport(p) {
      var f = this.features(p);
      return !!(f && f.priority_support);
    },
    isFreePlan(p) {
      return (
        p.name.toLowerCase() === "free" ||
        p.id === 1 ||
        p.price === 0 ||
        p.price_monthly === 0
      );
    }
  }
};
</script>

<style scoped>
.plan-comparison {
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--border-cyan-20, rgba(56, 189, 248, 0.2));
  border-radius: 12px;
}

.comparison-table {
  background: transparent;
}

.comparison-table th,
.comparison-table td {
  white-space: nowrap;
}

.comparison-table .feature-col {
  text-align: left;
  color: var(--text-secondary);
  font-weight: 500;
  position: sticky;
  left: 0;
  background: var(--bg-card, #ffffff);
  z-index: 1;
}

.comparison-table thead th {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-heading);
  vertical-align: bottom;
  padding-top: 12px;
  padding-bottom: 12px;
}

.comparison-table thead th.feature-col {
  color: var(--text-secondary);
  font-weight: 600;
}

.comparison-table .th-price {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.comparison-table .pro-col {
  background: var(--light-bg-selected, #eff6ff);
}

.comparison-table .muted {
  color: var(--text-muted);
}
</style>
